import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { showCardTooltip, hideCardTooltip } from '@/components/CardTooltip';
import { cardImageUris, displayCardName, scryfallImageUri } from '@/lib/utils';
import { renderManaSymbols } from '@/lib/text-pipeline';
import { filterCards } from '@/lib/deck-filter';
import type { CardMeta, CardEntry, TagGroup } from '@/lib/api';
import { CardAutocomplete } from '@/components/CardAutocomplete';
import { useRoutePrefs } from '@/hooks/useRoutePrefs';
import { hiddenColumns } from '@/lib/decklistColumns';

// ── Types ────────────────────────────────────────────────────────────────────

export interface DecklistWidgetProps {
  cards: Record<string, CardEntry>;
  cardMeta: Record<string, CardMeta>;
  commanders?: string[];
  /** Commander color identity — filters autocomplete to legal cards. */
  colorIdentity?: string[];
  compact?: boolean;
  canEdit?: boolean;
  onEdit?: () => void;
  /** Which group modes to show in toolbar. Defaults to ['type', 'alpha', 'tags']. */
  groupModes?: GroupMode[];
  /** Default group mode. Defaults to first in groupModes. */
  defaultGroupMode?: GroupMode;
  /** Backend tag groups (metacommand/deck/tag_grouping.py). Group-by-tag is offered only when supplied. */
  tagGroups?: TagGroup[];
  /** First-run default for the set code column (the user's saved choice wins). */
  showSetColumn?: boolean;
  /** First-run default for the rarity column (the user's saved choice wins). */
  showRarityColumn?: boolean;
  /**
   * localStorage key for the toolbar preferences (group, sort, columns, view). Global, not
   * per route or deck, so a choice in one deck is the default for the next. Defaults to 'decklist'.
   */
  prefsKey?: string;
  /** When true, shows tag chips on each row and group headers as DnD drop zones. */
  editTagsMode?: boolean;
  onEditTagsToggle?: () => void;
  onCardTagAdd?: (cardName: string, tag: string) => void;
  onCardTagRemove?: (cardName: string, tag: string) => void;
  /** Inline deck editing mode — shows +/- qty controls and repurposes filter as Add card. */
  editDeckMode?: boolean;
  onCardQtyChange?: (cardName: string, delta: number) => void;
  /** Starred card names; a favorite is painted gold and bold. */
  favorites?: Set<string>;
  /** Favorites starred on the base deck, not this variant: the star shows filled but locked (no callback). */
  inheritedFavorites?: Set<string>;
  /** Edit mode shows a star per row that calls this. */
  onToggleFavorite?: (cardName: string) => void;
  onCardAdd?: (cardName: string) => void;
  onBulkEdit?: () => void;
  /** Called when the user sets/clears the commander(s) in edit mode. */
  onSetCommander?: (names: string[]) => void;
  /** Start the entire widget in a collapsed state (user can expand). */
  defaultCollapsed?: boolean;
  /** Per-card delta status for Δ-view highlighting. 'added' → green, 'override' → amber, 'cut' → ghost row only. */
  cardStatus?: Record<string, 'added' | 'cut' | 'override'>;
  /** Cut cards to render as struck-through ghost rows at the bottom of the list view. */
  ghostRows?: { name: string; qty: number }[];
  /** Right-click / kebab on a row: open the printing picker for that card. */
  onCardContextMenu?: (cardName: string, e: React.MouseEvent) => void;
  /** One labelled button at the end of each LIST-view row (never image/tile view); `hidden` drops it for a row. */
  rowAction?: { label: string; onClick: (cardName: string) => void; hidden?: (cardName: string) => boolean };
}

type GroupMode = 'type' | 'alpha' | 'tags' | 'set';
type SortField = 'name' | 'mana' | 'color' | 'cost';
type SortDir = 'asc' | 'desc';
type SortMode = { field: SortField; dir: SortDir } | null;
type ViewMode = 'list' | 'image' | 'tile';

/** The toolbar choices, persisted under one key via useRoutePrefs. */
interface DecklistPrefs {
  groupMode: GroupMode;
  sortMode: SortMode;
  showPrices: boolean;
  showRarity: boolean;
  showSet: boolean;
  viewMode: ViewMode;
}

const VIEW_MODES: readonly ViewMode[] = ['list', 'image', 'tile'];
const SORT_FIELDS: readonly SortField[] = ['name', 'mana', 'color', 'cost'];
const DEFAULT_SORT: SortMode = { field: 'name', dir: 'asc' };

interface CardRow {
  name: string;
  qty: number;
  scryfall_id?: string;
}

interface CardGroup {
  header: string;
  count: number;
  cards: CardRow[];
  /** MDFC cards whose back face is a land (e.g. Jwari Disruption // Jwari Ruins) */
  mdfcLandCount?: number;
  /** Tag mode: the cards in this group by user tag (Edit Tags can remove only these). */
  userCards?: ReadonlySet<string>;
}

const OTHER_GROUP = 'Other';

// ── Constants ────────────────────────────────────────────────────────────────

/** Cards allowed in any quantity in Commander (rule exception). */
const ANY_NUMBER_CARDS_FE = new Set([
  'Shadowborn Apostle', "Dragon's Approach", 'Slime Against Humanity',
  'Hare Apparent', 'Persistent Petitioners', 'Rat Colony',
  'Relentless Rats', 'Seven Dwarves', 'Nazgûl',
]);

const TYPE_ORDER = [
  'Battle', 'Planeswalker', 'Creature', 'Instant', 'Sorcery',
  'Artifact', 'Enchantment', 'Land', 'Unknown',
] as const;


const WUBRG_ORDER: Record<string, number> = { W: 0, U: 1, B: 2, R: 3, G: 4 };

// ── Sort helpers ─────────────────────────────────────────────────────────────

/** True if a card is eligible to be a Commander in the format. */
function canBeCommander(meta: CardMeta | undefined): boolean {
  if (!meta) return false;
  // Explicit oracle text rule (e.g. Kenrith, planeswalkers with the clause)
  if (/\bcan be your commander\b/i.test(meta.oracle_text ?? '')) return true;
  // Must be Legendary for all other cases
  if (!meta.supertypes?.includes('Legendary')) return false;
  // Legendary Creature
  if (meta.types?.includes('Creature')) return true;
  // Legendary Enchantment with Background subtype [rule:702.124]
  if (meta.subtypes?.includes('Background')) return true;
  // Legendary Vehicle or Spaceship (Artifact subtypes)
  if (meta.subtypes?.includes('Vehicle') || meta.subtypes?.includes('Spaceship')) return true;
  return false;
}

function colorWeight(meta: CardMeta | undefined): number {
  const colors = meta?.colors ?? [];
  if (colors.length === 0) return -1; // colorless first
  if (colors.length > 1) return 5;    // multicolor last
  return WUBRG_ORDER[colors[0]] ?? 5;
}

function sortCards(
  rows: CardRow[],
  mode: SortMode,
  cardMeta: Record<string, CardMeta>,
  cards?: Record<string, CardEntry>,
): CardRow[] {
  if (!mode) return [...rows]; // no sort — preserve original order
  const dir = mode.dir === 'desc' ? -1 : 1;
  const sorted = [...rows];
  switch (mode.field) {
    case 'name':
      sorted.sort((a, b) => dir * a.name.localeCompare(b.name));
      break;
    case 'mana':
      sorted.sort((a, b) => {
        const mv = (cardMeta[a.name]?.manaValue ?? 0) - (cardMeta[b.name]?.manaValue ?? 0);
        return dir * (mv !== 0 ? mv : a.name.localeCompare(b.name));
      });
      break;
    case 'color':
      sorted.sort((a, b) => {
        const cw = colorWeight(cardMeta[a.name]) - colorWeight(cardMeta[b.name]);
        return dir * (cw !== 0 ? cw : a.name.localeCompare(b.name));
      });
      break;
    case 'cost':
      sorted.sort((a, b) => {
        const pa = getCardPrice(a.name, cards ?? {}, cardMeta[a.name]) ?? -1;
        const pb = getCardPrice(b.name, cards ?? {}, cardMeta[b.name]) ?? -1;
        return dir * (pa !== pb ? pa - pb : a.name.localeCompare(b.name));
      });
      break;
  }
  return sorted;
}

function cycleSortMode(current: SortMode, field: SortField): SortMode {
  if (!current || current.field !== field) return { field, dir: 'asc' };
  if (current.dir === 'asc') return { field, dir: 'desc' };
  return null; // desc → off
}

// ── Grouping logic ───────────────────────────────────────────────────────────

function buildCommanderGroup(
  cards: Record<string, CardEntry>,
  commanders: string[],
): CardGroup | null {
  const commanderSet = new Set(commanders);
  const commanderCards = Object.entries(cards)
    .filter(([name]) => commanderSet.has(name))
    .map(([name, e]) => ({ name, qty: e.qty, scryfall_id: e.scryfall_id }))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (commanderCards.length === 0) return null;
  return {
    header: 'Commander',
    count: commanderCards.reduce((s, c) => s + c.qty, 0),
    cards: commanderCards,
  };
}

function groupByType(
  cards: Record<string, CardEntry>,
  cardMeta: Record<string, CardMeta>,
  commanders: string[],
  sortMode: SortMode,
): CardGroup[] {
  const commanderSet = new Set(commanders);
  const groups: CardGroup[] = [];

  const cmdGroup = buildCommanderGroup(cards, commanders);
  if (cmdGroup) groups.push(cmdGroup);

  const buckets: Record<string, CardRow[]> = {};
  for (const [name, entry] of Object.entries(cards)) {
    if (commanderSet.has(name)) continue;
    const meta = cardMeta[name];
    const types = meta?.types ?? [];
    let primary = 'Unknown';
    // Land wins over Enchantment/Artifact/etc., but Creature wins over Land
    // (e.g. Urza's Saga → Lands; Dryad Arbor → Creatures)
    if (types.includes('Land') && !types.includes('Creature')) {
      primary = 'Land';
    } else {
      for (const t of TYPE_ORDER) {
        if (types.includes(t)) { primary = t; break; }
      }
    }
    if (!buckets[primary]) buckets[primary] = [];
    buckets[primary].push({ name, qty: entry.qty, scryfall_id: entry.scryfall_id });
  }

  for (const type of TYPE_ORDER) {
    const entries = buckets[type];
    if (!entries || entries.length === 0) continue;
    const sorted = sortCards(entries, sortMode, cardMeta, cards);
    const plural = type === 'Sorcery' ? 'Sorceries'
      : type === 'Unknown' ? 'Other'
      : type + 's';
    groups.push({
      header: plural,
      count: sorted.reduce((s, c) => s + c.qty, 0),
      cards: sorted,
    });
  }

  // Annotate the Lands group with MDFC land count (back face is a land)
  const mdfcLandCount = Object.entries(cards).reduce((sum, [name, entry]) => {
    if (commanderSet.has(name)) return sum;
    const meta = cardMeta[name];
    if (!meta) return sum;
    if (meta.layout === 'modal_dfc' && !meta.types.includes('Land') && meta.type?.includes('// Land')) {
      return sum + entry.qty;
    }
    return sum;
  }, 0);
  if (mdfcLandCount > 0) {
    const landsGroup = groups.find(g => g.header === 'Lands');
    if (landsGroup) landsGroup.mdfcLandCount = mdfcLandCount;
  }

  return groups;
}

function groupByAlpha(
  cards: Record<string, CardEntry>,
  cardMeta: Record<string, CardMeta>,
  commanders: string[],
  sortMode: SortMode,
): CardGroup[] {
  const commanderSet = new Set(commanders);
  const groups: CardGroup[] = [];

  const cmdGroup = buildCommanderGroup(cards, commanders);
  if (cmdGroup) groups.push(cmdGroup);

  const nonCmdr = Object.entries(cards)
    .filter(([name]) => !commanderSet.has(name))
    .map(([name, e]) => ({ name, qty: e.qty, scryfall_id: e.scryfall_id }));

  const sorted = sortCards(nonCmdr, sortMode, cardMeta, cards);

  if (sorted.length > 0) {
    groups.push({
      header: 'Main Deck',
      count: sorted.reduce((s, c) => s + c.qty, 0),
      cards: sorted,
    });
  }

  return groups;
}

function groupByTags(
  cards: Record<string, CardEntry>,
  cardMeta: Record<string, CardMeta>,
  commanders: string[],
  sortMode: SortMode,
  tagGroups: TagGroup[],
): CardGroup[] {
  const commanderSet = new Set(commanders);
  const groups: CardGroup[] = [];

  const cmdGroup = buildCommanderGroup(cards, commanders);
  if (cmdGroup) groups.push(cmdGroup);

  const toRows = (names: Iterable<string>): CardRow[] => {
    const rows: CardRow[] = [];
    for (const name of names) {
      const entry = cards[name];
      if (entry && !commanderSet.has(name)) rows.push({ name, qty: entry.qty, scryfall_id: entry.scryfall_id });
    }
    return sortCards(rows, sortMode, cardMeta, cards);
  };
  const push = (header: string, rows: CardRow[], userCards?: ReadonlySet<string>) => {
    if (rows.length > 0) groups.push({ header, count: rows.reduce((s, c) => s + c.qty, 0), cards: rows, userCards });
  };

  // The backend orders the groups (alphabetical, "Other" last); render them as given.
  const mentioned = new Set<string>();
  let other: TagGroup | undefined;
  for (const g of tagGroups) {
    g.cards.forEach(n => mentioned.add(n));
    if (g.kind === 'other') { other = g; continue; }
    push(g.label, toRows(g.cards), new Set(g.user_cards));
  }
  // A card the groups don't mention yet (added inline before the refetch) joins "Other".
  const unmentioned = Object.keys(cards).filter(n => !mentioned.has(n));
  push(OTHER_GROUP, toRows([...(other?.cards ?? []), ...unmentioned]));
  return groups;
}

function groupBySet(
  cards: Record<string, CardEntry>,
  cardMeta: Record<string, CardMeta>,
  commanders: string[],
  sortMode: SortMode,
): CardGroup[] {
  const commanderSet = new Set(commanders);
  const groups: CardGroup[] = [];

  const cmdGroup = buildCommanderGroup(cards, commanders);
  if (cmdGroup) groups.push(cmdGroup);

  const buckets: Record<string, CardRow[]> = {};
  const setDates: Record<string, string> = {}; // header → released_at for sorting

  for (const [name, entry] of Object.entries(cards)) {
    if (commanderSet.has(name)) continue;
    const meta = cardMeta[name];
    const setLabel = meta?.set_name || meta?.set_code?.toUpperCase() || 'Unknown Set';
    if (!buckets[setLabel]) buckets[setLabel] = [];
    buckets[setLabel].push({ name, qty: entry.qty, scryfall_id: entry.scryfall_id });
    // Track the latest release date per set for sort ordering
    // (all cards in a set share the same date, but we just overwrite — it's the same value)
    if (meta?.set_code) {
      // We don't have released_at in CardMeta, so we sort alphabetically by set name.
      // If the caller wants release-date ordering, they can pre-sort the cards record.
      setDates[setLabel] = setDates[setLabel] ?? '';
    }
  }

  // Sort set groups alphabetically (NewCardsPanel pre-orders by release date via set badge filtering)
  const setHeaders = Object.keys(buckets).sort((a, b) => a.localeCompare(b));

  for (const header of setHeaders) {
    const entries = buckets[header];
    if (!entries || entries.length === 0) continue;
    const sorted = sortCards(entries, sortMode, cardMeta, cards);
    groups.push({
      header,
      count: sorted.reduce((s, c) => s + c.qty, 0),
      cards: sorted,
    });
  }

  return groups;
}

// ── Preferences ─────────────────────────────────────────────────────────────

// Pre-2026-10-09 per-column keys. They only seed the first read of the new store,
// so existing users keep their column and view choices.
const LEGACY_PRICE_KEY = 'mtgagent_show_prices';
const LEGACY_RARITY_KEY = 'mtgagent_show_rarity';
const LEGACY_SET_KEY = 'mtgagent_show_set';
const LEGACY_VIEW_KEY = 'mtgagent_card_view';

function readLegacy(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function firstRunPrefs(
  groupMode: GroupMode,
  showSetColumn: boolean,
  showRarityColumn: boolean,
): DecklistPrefs {
  const legacyView = readLegacy(LEGACY_VIEW_KEY) as ViewMode | null;
  return {
    groupMode,
    sortMode: DEFAULT_SORT,
    showPrices: readLegacy(LEGACY_PRICE_KEY) === '1',
    showRarity: showRarityColumn || readLegacy(LEGACY_RARITY_KEY) === '1',
    showSet: showSetColumn || readLegacy(LEGACY_SET_KEY) === '1',
    viewMode: legacyView && VIEW_MODES.includes(legacyView) ? legacyView : 'list',
  };
}

/**
 * A stored sort that no longer parses falls back to name-ascending, as does a cost sort
 * while prices are hidden (the cost sort button only exists with prices on). A valid one
 * is returned as-is so its identity stays stable for the grouping memo.
 */
function validSortMode(mode: unknown, showPrices: boolean): SortMode {
  if (mode === null) return null;
  const m = mode as { field?: unknown; dir?: unknown } | undefined;
  if (m && SORT_FIELDS.includes(m.field as SortField) && (m.dir === 'asc' || m.dir === 'desc')
      && (m.field !== 'cost' || showPrices)) {
    return mode as SortMode;
  }
  return DEFAULT_SORT;
}

// ── Price helpers ───────────────────────────────────────────────────────────

function getCardPrice(
  cardName: string,
  cards: Record<string, CardEntry>,
  meta: CardMeta | undefined,
): number | null {
  if (!meta) return null;
  const finish = cards[cardName]?.finish;
  if (finish === 'foil' && meta.price_usd_foil != null) return meta.price_usd_foil;
  return meta.price_usd ?? meta.price_usd_foil ?? null;
}

function sumGroupPrice(
  groupCards: CardRow[],
  cards: Record<string, CardEntry>,
  cardMeta: Record<string, CardMeta>,
): number {
  let total = 0;
  for (const card of groupCards) {
    const price = getCardPrice(card.name, cards, cardMeta[card.name]);
    if (price != null) total += price * card.qty;
  }
  return total;
}

// ── Toolbar button ──────────────────────────────────────────────────────────

function ToolbarBtn({
  icon,
  title,
  active,
  onClick,
}: {
  icon: string;
  title: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`inline-flex items-center justify-center w-7 h-7 rounded-md border-none cursor-pointer transition-colors text-[16px] ${
        active
          ? 'bg-primary text-on-primary'
          : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest'
      }`}
      title={title}
      onClick={onClick}
      type="button"
    >
      <span className="material-icons text-[16px]">{icon}</span>
    </button>
  );
}

function SortBtn({
  icon,
  label,
  field,
  current,
  onClick,
}: {
  icon: string;
  label: string;
  field: SortField;
  current: SortMode;
  onClick: () => void;
}) {
  const active = current?.field === field;
  const arrow = active ? (current.dir === 'asc' ? '↑' : '↓') : '';
  return (
    <button
      className={`inline-flex items-center justify-center h-7 rounded-md border-none cursor-pointer transition-colors text-[16px] ${
        active
          ? 'bg-primary text-on-primary px-1.5'
          : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest w-7'
      }`}
      title={`Sort by ${label}${active ? ` (${current.dir})` : ''}`}
      onClick={onClick}
      type="button"
    >
      <span className="material-icons text-[16px]">{icon}</span>
      {arrow && <span className="text-[10px] font-bold ml-px leading-none">{arrow}</span>}
    </button>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function DecklistWidget({
  cards, cardMeta, commanders = [], colorIdentity, compact = false, canEdit = false, onEdit,
  groupModes = ['type', 'alpha', 'tags'],
  defaultGroupMode,
  tagGroups,
  showSetColumn = false,
  showRarityColumn = false,
  prefsKey = 'decklist',
  editTagsMode = false,
  onEditTagsToggle,
  onCardTagAdd,
  onCardTagRemove,
  editDeckMode = false,
  onCardQtyChange,
  favorites,
  inheritedFavorites,
  onToggleFavorite,
  onCardAdd,
  onBulkEdit,
  onSetCommander,
  defaultCollapsed = false,
  cardStatus,
  ghostRows,
  onCardContextMenu,
  rowAction,
}: DecklistWidgetProps) {
  const [widgetCollapsed, setWidgetCollapsed] = useState(defaultCollapsed);
  const [filter, setFilter] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // Defaults apply on the first run for this key only; useRoutePrefs reads storage once at mount.
  const [prefDefaults] = useState(() => firstRunPrefs(
    defaultGroupMode ?? groupModes[0] ?? 'type', showSetColumn, showRarityColumn,
  ));
  const [prefs, setPrefs] = useRoutePrefs<DecklistPrefs>(prefsKey, prefDefaults);
  const showPrices = !!prefs.showPrices;
  const sortMode = validSortMode(prefs.sortMode, showPrices);
  const viewMode: ViewMode = VIEW_MODES.includes(prefs.viewMode) ? prefs.viewMode : 'list';
  const showRarity = !!prefs.showRarity;
  const showSet = !!prefs.showSet;
  const [draggingCard, setDraggingCard] = useState<string | null>(null);
  const [dragOverGroup, setDragOverGroup] = useState<string | null>(null);
  const [dragOverCommander, setDragOverCommander] = useState(false);
  const [showSyntaxHelp, setShowSyntaxHelp] = useState(false);
  // Saved state to restore when exiting editTagsMode / editDeckMode
  const savedTagsState = useRef<{ filter: string } | null>(null);
  const savedDeckState = useRef<{ filter: string } | null>(null);
  const toggleViewMode = (mode: ViewMode) => setPrefs({ viewMode: mode });
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);

  // "/" keyboard shortcut to focus search
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key !== '/') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.target as HTMLElement)?.isContentEditable) return;
      e.preventDefault();
      searchWrapperRef.current?.querySelector('input')?.focus();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Save filter on entering editTagsMode, restore on exit
  useEffect(() => {
    if (editTagsMode) {
      savedTagsState.current = { filter };
      setFilter('');
    } else if (savedTagsState.current) {
      setFilter(savedTagsState.current.filter);
      savedTagsState.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTagsMode]);

  // Save filter on entering editDeckMode, restore on exit
  useEffect(() => {
    if (editDeckMode) {
      savedDeckState.current = { filter };
      setFilter('');
    } else if (savedDeckState.current) {
      setFilter(savedDeckState.current.filter);
      savedDeckState.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDeckMode]);

  // Click-outside to dismiss syntax help popover
  useEffect(() => {
    if (!showSyntaxHelp) return;
    function handleClick(e: MouseEvent) {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) {
        setShowSyntaxHelp(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSyntaxHelp]);

  const filteredCards = useMemo(() => {
    return filterCards(cards, cardMeta, commanders, filter);
  }, [cards, cardMeta, commanders, filter]);

  // Group-by-tag needs backend groups. A stored mode this mount doesn't offer (another
  // panel's mode, or tags before the groups load) falls back without being overwritten.
  const offeredModes = tagGroups ? groupModes : groupModes.filter(m => m !== 'tags');
  const fallbackMode: GroupMode = defaultGroupMode && offeredModes.includes(defaultGroupMode)
    ? defaultGroupMode
    : offeredModes[0] ?? 'type';
  const groupMode: GroupMode = offeredModes.includes(prefs.groupMode) ? prefs.groupMode : fallbackMode;
  const setGroupMode = (mode: GroupMode) => setPrefs({ groupMode: mode });
  // editTagsMode forces tags grouping without touching the saved preference.
  const effectiveGroupMode: GroupMode = editTagsMode ? 'tags' : groupMode;

  // Suppress extra columns in editTagsMode or editDeckMode for clean layout
  const suppressColumns = editTagsMode || editDeckMode;

  const groups = useMemo(() => {
    switch (effectiveGroupMode) {
      case 'alpha':
        return groupByAlpha(filteredCards, cardMeta, commanders, sortMode);
      case 'tags':
        return groupByTags(filteredCards, cardMeta, commanders, sortMode, tagGroups ?? []);
      case 'set':
        return groupBySet(filteredCards, cardMeta, commanders, sortMode);
      case 'type':
      default:
        return groupByType(filteredCards, cardMeta, commanders, sortMode);
    }
  }, [filteredCards, cardMeta, commanders, effectiveGroupMode, sortMode, tagGroups]);


  const totalCards = useMemo(
    () => Object.values(cards).reduce((s, e) => s + e.qty, 0),
    [cards],
  );

  const filteredTotal = useMemo(
    () => Object.values(filteredCards).reduce((s, e) => s + e.qty, 0),
    [filteredCards],
  );

  const toggleGroup = useCallback((header: string) => {
    if (compact) return;
    setCollapsed(prev => ({ ...prev, [header]: !prev[header] }));
  }, [compact]);

  const isCommander = useMemo(() => new Set(commanders), [commanders]);

  // Hiding prices drops a cost sort back to the default (and saves that), so turning
  // prices on again does not bring back a sort the user could not see.
  const togglePrices = () => setPrefs(
    showPrices && sortMode?.field === 'cost' ? { showPrices: false, sortMode: DEFAULT_SORT } : { showPrices: !showPrices },
  );
  const toggleRarity = () => setPrefs({ showRarity: !showRarity });
  const toggleSet = () => setPrefs({ showSet: !showSet });

  const handleSortClick = (field: SortField) => setPrefs({ sortMode: cycleSortMode(sortMode, field) });

  // Narrow rows drop optional columns so the name keeps its floor. Every row list has the
  // same width (each sits in one CSS column of .dlw-groups, or spans the compact panel), so
  // ONE ResizeObserver on the groups wrapper suffices: on any wrapper resize, including the
  // height change when rows first appear or a group expands, read the first row list's
  // width. Until measured (or without ResizeObserver, e.g. jsdom) nothing is hidden.
  const [groupsEl, setGroupsEl] = useState<HTMLDivElement | null>(null);
  const [rowWidth, setRowWidth] = useState(0);
  useEffect(() => {
    if (!groupsEl || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const rows = groupsEl.querySelector('.dlw-rows');
      if (rows) setRowWidth(Math.floor(rows.getBoundingClientRect().width));
    });
    ro.observe(groupsEl);
    return () => ro.disconnect();
  }, [groupsEl]);
  const hideClasses = useMemo(() => {
    // Edit modes render none of the optional columns; a hidden panel measures 0.
    if (suppressColumns || rowWidth <= 0) return '';
    const enabled = { price: showPrices, rarity: showRarity, set: showSet, kebab: !!onCardContextMenu, action: !!rowAction };
    return [...hiddenColumns(rowWidth, enabled, compact)].map(col => `dlw-hide-${col}`).join(' ');
  }, [suppressColumns, rowWidth, showPrices, showRarity, showSet, onCardContextMenu, rowAction, compact]);

  // Deck validity issues — only computed in editDeckMode to avoid perf cost during browsing
  const deckValidation = useMemo(() => {
    const qtyViolations = new Set<string>();
    if (!editDeckMode) return { issues: [], qtyViolations };
    const issues: string[] = [];
    const allEntries = Object.entries(cards);
    const total = allEntries.reduce((s, [, e]) => s + e.qty, 0);
    if (total !== 100) issues.push(`${total}/100 cards — Commander requires exactly 100`);
    const deckCI = new Set(colorIdentity ?? []);
    for (const [name, entry] of allEntries) {
      const meta = cardMeta?.[name];
      if (!meta) continue;
      if (meta.banned) issues.push(`${name} is banned in Commander`);
      if (deckCI.size > 0 && (meta.colorIdentity?.length ?? 0) > 0) {
        if (meta.colorIdentity!.some(c => !deckCI.has(c))) {
          issues.push(`${name} is outside the deck's color identity`);
        }
      }
      if (entry.qty > 1) {
        const isBasic = meta.supertypes?.includes('Basic') && meta.types?.includes('Land');
        if (!isBasic && !ANY_NUMBER_CARDS_FE.has(name)) qtyViolations.add(name);
      }
    }
    if (qtyViolations.size > 0) {
      issues.push(`${[...qtyViolations].join(', ')} — only 1 copy allowed`);
    }
    return { issues, qtyViolations };
  }, [editDeckMode, cards, cardMeta, colorIdentity]);

  const deckTotal = useMemo(() => {
    if (!showPrices) return 0;
    let total = 0;
    for (const [name, entry] of Object.entries(cards)) {
      const price = getCardPrice(name, cards, cardMeta[name]);
      if (price != null) total += price * entry.qty;
    }
    return total;
  }, [showPrices, cards, cardMeta]);

  return (
    <div className={`panel-card text-on-surface ${compact ? 'p-spacing-sm text-xs' : 'p-spacing-md text-md'}`}>
      {/* Widget-level collapse bar */}
      {defaultCollapsed !== undefined && (
        <div
          className="flex items-center justify-between cursor-pointer select-none mb-2"
          onClick={() => setWidgetCollapsed(v => !v)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setWidgetCollapsed(v => !v); } }}
        >
          <span className="text-sm font-medium text-on-surface-variant">
            {widgetCollapsed ? `Decklist (${totalCards} cards)` : 'Decklist'}
          </span>
          <span className="material-icons text-[18px] text-on-surface-variant">
            {widgetCollapsed ? 'expand_more' : 'expand_less'}
          </span>
        </div>
      )}
      {widgetCollapsed ? null : (<>
      {/* Toolbar + Search — sticky in compact/sidebar mode */}
      <div className={compact ? 'sticky top-0 z-10 bg-surface-container pb-1' : ''}>
        {/* Toolbar — a wrapping line of clusters. Each cluster draws its own left separator;
            the outer overflow-hidden clips the separator of whichever cluster starts a line
            (see .dlw-toolbar in index.css), so narrow panels wrap instead of spilling. */}
        <div className={`overflow-hidden py-0.5 ${compact ? 'mb-0.5' : 'mb-1.5'}`}>
        <div className={`dlw-toolbar flex flex-wrap items-center gap-y-1 ${compact ? 'dlw-toolbar-compact' : ''}`}>
          {/* Group mode */}
          <div className="dlw-tool-cluster">
            {!compact && <span className="text-xxs text-on-surface-variant uppercase tracking-wider mr-0.5">Group</span>}
            {offeredModes.includes('type') && (
              <ToolbarBtn icon="view_list" title="Group by type" active={groupMode === 'type'} onClick={() => setGroupMode('type')} />
            )}
            {offeredModes.includes('set') && (
              <ToolbarBtn icon="inventory_2" title="Group by set" active={groupMode === 'set'} onClick={() => setGroupMode('set')} />
            )}
            {offeredModes.includes('alpha') && (
              <ToolbarBtn icon="sort_by_alpha" title="Alphabetical (single group)" active={groupMode === 'alpha'} onClick={() => setGroupMode('alpha')} />
            )}
            {offeredModes.includes('tags') && (
              <ToolbarBtn icon="sell" title="Group by tag" active={groupMode === 'tags'} onClick={() => setGroupMode('tags')} />
            )}
          </div>

          {/* Sort mode — tri-state: off → asc → desc */}
          <div className="dlw-tool-cluster">
            {!compact && <span className="text-xxs text-on-surface-variant uppercase tracking-wider mr-0.5">Sort</span>}
            <SortBtn icon="sort_by_alpha" label="name" field="name" current={sortMode} onClick={() => handleSortClick('name')} />
            <SortBtn icon="bar_chart" label="mana value" field="mana" current={sortMode} onClick={() => handleSortClick('mana')} />
            <SortBtn icon="palette" label="color" field="color" current={sortMode} onClick={() => handleSortClick('color')} />
            {showPrices && (
              <SortBtn icon="attach_money" label="cost" field="cost" current={sortMode} onClick={() => handleSortClick('cost')} />
            )}
          </div>

          {/* Column toggles */}
          <div className="dlw-tool-cluster">
            <ToolbarBtn icon="attach_money" title="Toggle prices" active={showPrices} onClick={togglePrices} />
            <ToolbarBtn icon="diamond" title="Toggle rarity" active={showRarity} onClick={toggleRarity} />
            <ToolbarBtn icon="inventory_2" title="Toggle set" active={showSet} onClick={toggleSet} />
          </div>

          {/* Edit deck button — owner/admin only */}
          {canEdit && onEdit && (
            <div className="dlw-tool-cluster">
              <ToolbarBtn icon="edit" title={editDeckMode ? 'Stop editing' : 'Edit deck'} active={editDeckMode} onClick={onEdit} />
              {editDeckMode && onBulkEdit && (
                <ToolbarBtn icon="article" title="Bulk edit (text editor)" active={false} onClick={onBulkEdit} />
              )}
            </div>
          )}

          {/* Edit tags button — owner only */}
          {onEditTagsToggle && (
            <div className="dlw-tool-cluster">
              <ToolbarBtn icon="label" title="Edit tags" active={editTagsMode} onClick={onEditTagsToggle} />
            </div>
          )}

          {/* View mode toggle — non-compact only */}
          {!compact && (
            <div className="dlw-tool-cluster">
              <ToolbarBtn icon="format_list_bulleted" title="List view"    active={viewMode === 'list'}  onClick={() => toggleViewMode('list')} />
              <ToolbarBtn icon="grid_view"            title="Image grid"   active={viewMode === 'image'} onClick={() => toggleViewMode('image')} />
              <ToolbarBtn icon="layers"               title="Title strips" active={viewMode === 'tile'}  onClick={() => toggleViewMode('tile')} />
            </div>
          )}
        </div>
        </div>

        {/* Search bar / Add card bar */}
        <div
          ref={searchWrapperRef}
          className={`flex items-center gap-1.5 bg-surface-container-high border rounded-[20px] px-[10px] ${
            editDeckMode ? 'border-primary' : 'border-outline-variant'
          } ${compact ? 'h-[26px] mb-1' : 'h-[30px] mb-3'}`}
          onKeyDown={editDeckMode && onCardAdd ? (e) => {
            if (e.key === 'Enter' && filter.trim()) {
              e.preventDefault();
              onCardAdd(filter.trim());
              setFilter('');
            }
          } : undefined}
        >
          <span className={`material-icons text-on-surface-variant shrink-0 ${compact ? 'text-[15px]' : 'text-[17px]'}`}>
            {editDeckMode ? 'add' : 'search'}
          </span>
          <CardAutocomplete
            value={filter}
            onChange={setFilter}
            placeholder={editDeckMode ? 'Add a card...' : 'Filter cards...'}
            tabComplete={true}
            colorIdentity={colorIdentity}
            wrapperClassName="flex-1 min-w-0"
            inputClassName={`w-full border-none bg-transparent text-on-surface outline-none ${compact ? 'text-xs' : 'text-base'}`}
          />
          {filter && (
            <button
              onClick={() => setFilter('')}
              className="bg-transparent border-none text-on-surface-variant cursor-pointer p-0"
              title="Clear filter"
            >
              <span className="material-icons text-lg">close</span>
            </button>
          )}
          {!compact && (
            <button
              onClick={() => setShowSyntaxHelp(v => !v)}
              className="bg-transparent border-none text-on-surface-variant hover:text-primary shrink-0 cursor-pointer p-0"
              title="Search syntax help"
              type="button"
            >
              <span className="material-icons text-[15px]">help_outline</span>
            </button>
          )}
        </div>

        {/* Search syntax help popover */}
        {showSyntaxHelp && !compact && (
          <div ref={helpRef} className="relative z-20">
            <div className="absolute right-0 top-1 w-[340px] bg-surface-container-high border border-outline-variant rounded-md shadow-lg p-3 text-xs">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold text-on-surface text-sm">Filter Syntax</span>
                <button
                  onClick={() => setShowSyntaxHelp(false)}
                  className="bg-transparent border-none text-on-surface-variant cursor-pointer p-0"
                  type="button"
                >
                  <span className="material-icons text-base">close</span>
                </button>
              </div>
              <table className="w-full text-xs">
                <tbody className="[&_td]:py-[2px] [&_td]:pr-2 [&_td:first-child]:text-primary [&_td:first-child]:font-mono [&_td:first-child]:whitespace-nowrap">
                  <tr><td>sol ring</td><td className="text-on-surface-variant">Name contains text</td></tr>
                  <tr><td>t:creature</td><td className="text-on-surface-variant">Type line contains</td></tr>
                  <tr><td>o:&quot;first strike&quot;</td><td className="text-on-surface-variant">Oracle text contains</td></tr>
                  <tr><td>c:WG</td><td className="text-on-surface-variant">Casting cost includes colors</td></tr>
                  <tr><td>mv:&gt;3</td><td className="text-on-surface-variant">Mana value (=, &gt;, &lt;, &gt;=, &lt;=)</td></tr>
                  <tr><td>pow:&gt;=4</td><td className="text-on-surface-variant">Power comparison</td></tr>
                  <tr><td>tou:&lt;=2</td><td className="text-on-surface-variant">Toughness comparison</td></tr>
                  <tr><td>is:legendary</td><td className="text-on-surface-variant">Legendary cards</td></tr>
                  <tr><td>is:gamechanger</td><td className="text-on-surface-variant">WotC Game Changers</td></tr>
                  <tr><td>r:&gt;=rare</td><td className="text-on-surface-variant">Rarity (c/u/r/m, =, &gt;, &lt;, &gt;=, &lt;=)</td></tr>
                  <tr><td>s:dft</td><td className="text-on-surface-variant">Set code or name</td></tr>
                  <tr><td>tag:ramp</td><td className="text-on-surface-variant">Card has tag</td></tr>
                  <tr><td>-t:land</td><td className="text-on-surface-variant">Negate with <span className="text-primary font-mono">-</span> prefix</td></tr>
                </tbody>
              </table>
              <div className="text-on-surface-variant mt-2 pt-2 border-t border-outline-variant">
                Combine predicates with spaces (AND logic). Press <kbd className="px-1 py-0.5 bg-surface-container rounded text-on-surface">/</kbd> to focus search.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filtered count */}
      {filter.trim() && (
        <div className="text-xs text-on-surface-variant mb-spacing-sm text-center">
          {filteredTotal} / {totalCards} cards
        </div>
      )}

      {/* Validation warnings — editDeckMode only */}
      {editDeckMode && deckValidation.issues.length > 0 && (
        <div className="mb-2 px-2 py-1.5 rounded border border-warning-text/30 bg-status-warning-bg flex flex-col gap-0.5">
          {deckValidation.issues.map((issue, i) => (
            <div key={i} className="text-xs text-warning-text">{issue}</div>
          ))}
        </div>
      )}

      {/* Commander drop zone — editDeckMode only */}
      {editDeckMode && onSetCommander && (
        <div
          className={`flex flex-col gap-1 mb-3 p-2 rounded-md border-2 border-dashed transition-colors ${
            dragOverCommander ? 'border-primary bg-primary/10' : 'border-outline-variant'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOverCommander(true); }}
          onDragLeave={() => setDragOverCommander(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOverCommander(false);
            const cardName = e.dataTransfer.getData('dlw-cmd-card');
            if (!cardName) return;
            const current = commanders ?? [];
            if (current.includes(cardName)) return;
            const next = current.length < 2 ? [...current, cardName] : [current[0], cardName];
            onSetCommander(next);
          }}
        >
          <div className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">
            Commander{commanders && commanders.length > 1 ? 's' : ''} — drag a card here or use ♛
          </div>
          {(!commanders || commanders.length === 0) ? (
            <div className="text-xs text-on-surface-variant italic">
              No commander set — drag a card here to assign one
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {commanders.map(name => (
                <span key={name} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 border border-primary text-primary text-xs font-semibold">
                  {name}
                  <button
                    type="button"
                    title="Remove commander"
                    className="ml-1 text-on-surface-variant hover:text-error-text cursor-pointer bg-transparent border-none leading-none text-sm"
                    onClick={() => onSetCommander(commanders.filter(c => c !== name))}
                  >×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Groups — CSS columns flow groups into multiple columns. The dlw-hide-* classes
          drop optional row columns that do not fit (see hideClasses above). */}
      <div ref={setGroupsEl} data-dlw-groups="" className={`${
        compact
          ? 'flex flex-col gap-1.5'
          : viewMode === 'image'
            ? 'flex flex-col gap-4'
            : 'dlw-groups'
      } ${hideClasses}`}>
        {groups.length === 0 && (
          <div className="text-center text-on-surface-variant text-sm p-spacing-md italic">
            No cards match &ldquo;{filter}&rdquo;
          </div>
        )}
        {/* Render base groups, plus a "＋ New Tag" sentinel when editTagsMode */}
        {[...groups, ...(editTagsMode ? [{ header: '＋ New Tag', count: 0, cards: [] }] : [])].map(group => {
          const isNewTagSentinel = group.header === '＋ New Tag';
          const isCollapsed = !!collapsed[group.header] && !compact;
          const groupTotal = showPrices ? sumGroupPrice(group.cards, cards, cardMeta) : 0;
          const isDragTarget = editTagsMode && onCardTagAdd && dragOverGroup === group.header;
          return (
            <div
              key={group.header}
              className={compact ? '' : 'mb-3'}
              onDragOver={editTagsMode && onCardTagAdd ? (e) => { e.preventDefault(); setDragOverGroup(group.header); } : undefined}
              onDragLeave={editTagsMode && onCardTagAdd ? () => setDragOverGroup(null) : undefined}
              onDrop={editTagsMode && onCardTagAdd ? (e) => {
                e.preventDefault();
                const card = e.dataTransfer.getData('dlw-card');
                if (card) {
                  if (isNewTagSentinel) {
                    const t = window.prompt('New tag name:');
                    if (t && t.trim()) onCardTagAdd(card, t.trim());
                  } else if (group.header !== OTHER_GROUP) {
                    onCardTagAdd(card, group.header);
                  }
                }
                setDragOverGroup(null);
              } : undefined}
            >
              {/* Group header — break-after-avoid keeps header with at least the first card */}
              <div
                className={`flex items-center gap-1.5 py-1 border-b mb-1 select-none break-after-avoid transition-colors ${
                  isDragTarget
                    ? 'border-primary bg-primary/10 rounded-t-sm'
                    : 'border-outline-variant'
                } ${isNewTagSentinel ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
                onClick={isNewTagSentinel ? undefined : () => toggleGroup(group.header)}
                role={compact || isNewTagSentinel ? undefined : 'button'}
                tabIndex={compact || isNewTagSentinel ? undefined : 0}
                onKeyDown={compact || isNewTagSentinel ? undefined : (e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(group.header); }
                }}
              >
                <span className={`font-semibold uppercase tracking-[0.5px] ${compact ? 'text-[10px]' : 'text-sm'} ${isNewTagSentinel ? 'text-on-surface-variant' : 'text-primary'}`}>{group.header}</span>
                {!isNewTagSentinel && (
                  <span className="text-xs text-on-surface-variant">
                    ({group.count}
                    {group.mdfcLandCount != null && group.mdfcLandCount > 0 && (
                      <span
                        className="text-primary font-semibold"
                        title="Additional MDFC lands (spell on front, land on back)"
                      > +{group.mdfcLandCount}</span>
                    )}
                    ){showPrices && groupTotal > 0 && ` · $${groupTotal.toFixed(2)}`}
                  </span>
                )}
                {!compact && !isNewTagSentinel && (
                  <span
                    className="material-icons text-lg text-on-surface-variant ml-auto transition-transform duration-200"
                    style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                  >
                    expand_more
                  </span>
                )}
              </div>

              {/* Card rows — list view. Optional columns carry .dlw-col-* so the wrapper's
                  dlw-hide-* classes can drop them (lib/decklistColumns.ts). */}
              {!isCollapsed && !isNewTagSentinel && viewMode === 'list' && (
                <div className="dlw-rows flex flex-col">
                  {group.cards.map(card => {
                    const meta = cardMeta[card.name];
                    const isCmd = isCommander.has(card.name);
                    const manaCostHtml = (!suppressColumns && meta?.manaCost) ? renderManaSymbols(meta.manaCost) : '';
                    return (
                      <div
                        key={card.name}
                        className={`flex flex-wrap items-baseline ${compact ? 'text-xs py-[1px] gap-x-1' : 'text-base py-[2px] gap-x-1.5'} ${(editTagsMode || editDeckMode) && draggingCard === card.name ? 'opacity-40' : ''}`}
                        draggable={(editTagsMode || (editDeckMode && !!onSetCommander)) || undefined}
                        onDragStart={(editTagsMode || (editDeckMode && !!onSetCommander)) ? (e) => {
                          e.dataTransfer.setData('dlw-card', card.name);
                          e.dataTransfer.setData('dlw-cmd-card', card.name);
                          e.dataTransfer.effectAllowed = 'copy';
                          setDraggingCard(card.name);
                        } : undefined}
                        onDragEnd={(editTagsMode || (editDeckMode && !!onSetCommander)) ? () => setDraggingCard(null) : undefined}
                        onContextMenu={onCardContextMenu ? (e) => { e.preventDefault(); onCardContextMenu(card.name, e); } : undefined}
                      >
                        {editDeckMode && onToggleFavorite && (() => {
                          const _starred = !!favorites?.has(card.name);
                          const _inherited = _starred && !!inheritedFavorites?.has(card.name);
                          return (
                            <button
                              type="button"
                              aria-pressed={_starred}
                              aria-label={`Favorite ${card.name}`}
                              aria-disabled={_inherited || undefined}
                              disabled={_inherited}
                              title={_inherited ? 'Starred on the base deck' : _starred ? 'Remove favorite' : 'Favorite'}
                              onClick={(e) => { e.stopPropagation(); if (!_inherited) onToggleFavorite(card.name); }}
                              className={`shrink-0 w-5 h-5 flex items-center justify-center rounded bg-transparent border-none text-sm leading-none ${_inherited ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                              style={{ color: _starred ? 'var(--favorite)' : undefined }}
                            >{_starred ? '★' : '☆'}</button>
                          );
                        })()}

                        {/* Quantity — plain number in normal mode, editable controls in editDeckMode */}
                        {editDeckMode && onCardQtyChange ? (
                          <span className="flex items-center gap-0.5 shrink-0">
                            <button
                              type="button"
                              className="qty-btn"
                              title="Remove one"
                              onClick={(e) => { e.stopPropagation(); onCardQtyChange(card.name, -1); }}
                            >−</button>
                            <span className={`tabular-nums text-center min-w-[1.25rem] text-xs ${deckValidation.qtyViolations.has(card.name) ? 'text-error-text font-semibold' : 'text-on-surface-variant'}`}>{card.qty}</span>
                            <button
                              type="button"
                              className="qty-btn"
                              title="Add one"
                              onClick={(e) => { e.stopPropagation(); onCardQtyChange(card.name, +1); }}
                            >+</button>
                          </span>
                        ) : (
                          <span className={`text-right shrink-0 ${compact ? 'min-w-4 text-[10px]' : 'min-w-5 text-sm'} ${deckValidation.qtyViolations.has(card.name) ? 'text-error-text font-semibold' : 'text-on-surface-variant'}`}>
                            {card.qty > 1 ? `${card.qty}x` : '\u00A0'}
                          </span>
                        )}

                        {/* Crown button — only for cards eligible to be commander */}
                        {editDeckMode && onSetCommander && canBeCommander(meta) && (
                          <button
                            type="button"
                            title={isCmd ? 'Remove as commander' : 'Set as commander'}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isCmd) {
                                onSetCommander((commanders ?? []).filter(c => c !== card.name));
                              } else {
                                const current = commanders ?? [];
                                const next = current.length < 2 ? [...current, card.name] : [current[0], card.name];
                                onSetCommander(next);
                              }
                            }}
                            className={`shrink-0 w-5 h-5 flex items-center justify-center rounded cursor-pointer bg-transparent border-none text-sm leading-none transition-colors ${
                              isCmd ? 'text-primary' : 'text-on-surface-variant/40 hover:text-primary/70'
                            }`}
                          >♛</button>
                        )}

                        {/* Card name */}
                        {(() => {
                          const _status = cardStatus?.[card.name];
                          const _statusStyle = _status === 'added'
                            ? { color: 'var(--success, #6bd08a)' }
                            : _status === 'override'
                            ? { color: 'var(--warning, #e6c060)' }
                            : undefined;
                          const _fav = !!favorites?.has(card.name);
                          const _nameStyle = _fav ? { ..._statusStyle, color: 'var(--favorite)' } : _statusStyle;
                          return (
                            <span
                              className={`decklist-card ${editTagsMode ? 'cursor-grab' : editDeckMode && onSetCommander ? 'cursor-grab' : 'cursor-help'} flex-1 min-w-[6ch] truncate ${_fav ? 'font-bold' : isCmd ? 'text-primary font-semibold' : ''}`}
                              data-card-name={card.name}
                              tabIndex={0}
                              onMouseEnter={(e) => showCardTooltip(cardImageUris(card.name, card.scryfall_id, meta?.layout), e)}
                              onMouseLeave={hideCardTooltip}
                              onFocus={(e) => showCardTooltip(cardImageUris(card.name, card.scryfall_id, meta?.layout), e)}
                              onBlur={hideCardTooltip}
                              style={_nameStyle}
                            >
                              {displayCardName(card.name, meta?.layout)}
                              {_status && <span className="ml-1 text-[10px] uppercase opacity-70">{_status}</span>}
                            </span>
                          );
                        })()}

                        {onCardContextMenu && (
                          <button
                            type="button"
                            aria-label={`Printings of ${card.name}`}
                            title="Choose printing"
                            className="shrink-0 w-5 h-5 flex items-center justify-center rounded bg-transparent border-none cursor-pointer text-on-surface-variant/40 hover:text-primary/70 text-sm leading-none"
                            onClick={(e) => { e.stopPropagation(); onCardContextMenu(card.name, e); }}
                          >⋮</button>
                        )}

                        {rowAction && !rowAction.hidden?.(card.name) && (
                          <button
                            type="button"
                            aria-label={`${rowAction.label}: ${card.name}`}
                            className="btn-ghost shrink-0 text-xs py-0 px-2"
                            onClick={(e) => { e.stopPropagation(); rowAction.onClick(card.name); }}
                          >{rowAction.label}</button>
                        )}

                        {/* Badges — hidden in edit modes for clean layout */}
                        {!suppressColumns && meta?.game_changer && (
                          <span className="text-xxs font-bold px-1 py-[1px] rounded-[3px] bg-status-warning-bg text-warning-text leading-none shrink-0 dlw-col-badges">GC</span>
                        )}
                        {!suppressColumns && meta?.banned && (
                          <span className="text-xxs font-bold px-1 py-[1px] rounded-[3px] bg-[var(--bracket-5)] text-white leading-none shrink-0 dlw-col-badges">Banned</span>
                        )}
                        {!suppressColumns && meta?.unreleased && (
                          <span className="text-xxs font-bold px-1 py-[1px] rounded-[3px] bg-[var(--primary)] text-on-primary leading-none shrink-0 dlw-col-badges">Unreleased</span>
                        )}
                        {!suppressColumns && meta?.in_deck && !meta?.reprint && (
                          <span className="text-xxs font-bold px-1 py-[1px] rounded-[3px] bg-surface-container-high text-on-surface-variant border border-outline-variant leading-none shrink-0 dlw-col-badges" title="Already in your deck">✓</span>
                        )}

                        {/* Rarity badge — hidden in edit modes */}
                        {!suppressColumns && showRarity && meta?.rarity && (
                          <span className={`dlw-col-rarity text-xxs shrink-0 uppercase tracking-wide font-medium ${
                            ({ common: 'text-on-surface-variant', uncommon: 'text-[#a8a8a8]', rare: 'text-[#d4a940]', mythic: 'text-[#e06020]' } as Record<string, string>)[meta.rarity] ?? 'text-on-surface-variant'
                          }`}>
                            {meta.rarity[0]}
                          </span>
                        )}

                        {/* Set code — one per row, only while the toggle is on; hidden in edit modes.
                            A pinned printing wins over the card's default set code. */}
                        {(() => {
                          if (suppressColumns || !showSet) return null;
                          const pinnedSet = cards[card.name]?.set;
                          const setCode = pinnedSet ?? meta?.set_code;
                          if (!setCode) return null;
                          return (
                            <span
                              title={pinnedSet ? 'Pinned printing' : undefined}
                              className="dlw-col-set text-xxs text-on-surface-variant shrink-0 uppercase"
                            >
                              {setCode.toUpperCase()}
                            </span>
                          );
                        })()}

                        {/* Price — hidden in edit modes */}
                        {!suppressColumns && showPrices && !compact && (() => {
                          const price = getCardPrice(card.name, cards, meta);
                          return (
                            <span className="dlw-col-price text-xs text-on-surface-variant shrink-0 tabular-nums min-w-[3rem] text-right">
                              {price != null ? `$${price.toFixed(2)}` : '\u2014'}
                            </span>
                          );
                        })()}

                        {/* Mana cost — hidden in edit modes */}
                        {!suppressColumns && manaCostHtml && !compact && (
                          <span
                            className="dlw-col-mana shrink-0 ml-auto whitespace-nowrap text-sm leading-none"
                            dangerouslySetInnerHTML={{ __html: manaCostHtml }}
                          />
                        )}

                        {/* Remove-from-tag button — editTagsMode only, only on user-tag memberships */}
                        {editTagsMode && onCardTagRemove && group.userCards?.has(card.name) && (
                          <button
                            type="button"
                            className="tag-row-remove shrink-0"
                            title={`Remove from ${group.header}`}
                            onClick={(e) => { e.stopPropagation(); onCardTagRemove(card.name, group.header); }}
                          >×</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Card images — image grid view */}
              {!isCollapsed && !isNewTagSentinel && viewMode === 'image' && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5 mb-1">
                  {group.cards.map(card => {
                    const meta = cardMeta[card.name];
                    const rotated = ['split', 'planar'].includes(meta?.layout ?? '');
                    return (
                      <div
                        key={card.name}
                        className={`relative rounded-sm overflow-hidden cursor-help ${rotated ? 'aspect-[7/5]' : 'aspect-[5/7]'}`}
                        onMouseEnter={(e) => showCardTooltip(cardImageUris(card.name, card.scryfall_id, meta?.layout), e)}
                        onMouseLeave={hideCardTooltip}
                      >
                        <img
                          src={scryfallImageUri(card.name, card.scryfall_id, 'normal')}
                          alt={card.name}
                          loading="lazy"
                          className="absolute"
                          style={rotated ? {
                            width: '100%', height: 'auto', maxWidth: 'none',
                            top: '50%', left: '50%',
                            transform: 'translate(-50%, -50%) rotate(90deg)',
                          } : { width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        {card.qty > 1 && (
                          <span className="absolute top-1 left-1 bg-black/75 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                            {card.qty}×
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Title strips — tile view */}
              {!isCollapsed && !isNewTagSentinel && viewMode === 'tile' && (
                <div className="flex flex-col gap-px mb-1">
                  {group.cards.map(card => {
                    const meta = cardMeta[card.name];
                    const rotated = ['split', 'planar'].includes(meta?.layout ?? '');
                    const imgSrc = scryfallImageUri(card.name, card.scryfall_id, 'normal');
                    return (
                      <div
                        key={card.name}
                        className="relative h-[44px] rounded-sm overflow-hidden cursor-help border border-white/5"
                        style={rotated ? undefined : {
                          backgroundImage: `url(${imgSrc})`,
                          backgroundSize: '100% auto',
                          backgroundPosition: 'top center',
                        }}
                        onMouseEnter={(e) => showCardTooltip(cardImageUris(card.name, card.scryfall_id, meta?.layout), e)}
                        onMouseLeave={hideCardTooltip}
                      >
                        {rotated && (
                          <img
                            src={imgSrc}
                            alt={card.name}
                            loading="lazy"
                            className="absolute"
                            style={{
                              width: '100%', height: 'auto', maxWidth: 'none',
                              top: '50%', left: '50%',
                              transform: 'translate(-50%, -50%) rotate(90deg)',
                            }}
                          />
                        )}
                        {card.qty > 1 && (
                          <span className="absolute top-1 right-1 bg-black/75 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                            {card.qty}×
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Ghost rows — cut cards shown as struck-through in Δ-view (list view only) */}
      {ghostRows && ghostRows.length > 0 && viewMode === 'list' && (
        <div className="flex flex-col gap-px mb-1 px-1">
          {ghostRows.map((g) => (
            <div key={`ghost-${g.name}`} className="flex items-center gap-2 opacity-60">
              <span className="decklist-card flex-1 min-w-0 truncate line-through"
                    data-card-name={g.name}
                    onMouseEnter={(e) => showCardTooltip(cardImageUris(g.name), e)}
                    onMouseLeave={hideCardTooltip}
                    style={{ color: 'var(--error, #e07a7a)' }}>
                {g.name}
              </span>
              <span className="text-[10px] uppercase opacity-70">cut</span>
            </div>
          ))}
        </div>
      )}

      {/* Total count + price */}
      <div className="text-sm text-on-surface-variant py-2 border-t border-outline-variant mt-spacing-sm text-center">
        {totalCards} cards total{showPrices && deckTotal > 0 && ` · $${deckTotal.toFixed(2)}`}
      </div>
      </>)}
    </div>
  );
}
